import React, { useState, useEffect } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import EntityDetailLayout from "../EntityDetailLayout";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { getProductById, Product, archiveProduct } from "@/data/productData";
import { formatCurrency, formatDate } from "@/utils/formatters";
import { Badge } from "@/components/ui/badge";
import { Copy, Archive, ExternalLink, ShieldAlert } from "lucide-react";
import {
  calculateStripeFee,
  calculatePlatformFee,
  calculateGrossFromNetSync,
} from "@/utils/fees";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { getPaymentUrl } from "@/config/urls";

interface ProductDetailsViewProps {
  id: string;
  onClose: () => void;
}

interface LinkedOfferPage {
  id: string;
  heading?: string;
  subheading?: string;
  is_test?: boolean;
  created_at?: string;
}

const ProductDetailsView: React.FC<ProductDetailsViewProps> = ({
  id,
  onClose,
}) => {
  const [product, setProduct] = useState<Product | null>(null);
  const [linkedOfferPages, setLinkedOfferPages] = useState<LinkedOfferPage[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [offerPagesLoading, setOfferPagesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Check if user is authenticated
  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getUser();
      setIsAuthenticated(!!data.user);
    };
    checkAuth();
  }, []);

  useEffect(() => {
    const fetchProductData = async () => {
      try {
        setLoading(true);

        if (!isAuthenticated) {
          setError("You must be logged in to view product details.");
          setLoading(false);
          return;
        }

        const productData = await getProductById(id);

        if (!productData) {
          setError(
            "Product not found or you do not have permission to view it."
          );
          return;
        }

        setProduct(productData);
      } catch (err) {
        console.error("Failed to fetch product:", err);
        setError("Failed to load product details");
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated !== null) {
      fetchProductData();
    }
  }, [id, isAuthenticated]);

  // Fetch linked offer pages
  useEffect(() => {
    const fetchLinkedOfferPages = async () => {
      if (!isAuthenticated || !product) return;

      try {
        setOfferPagesLoading(true);
        console.log("Fetching linked offer pages for product:", id);

        const { data, error } = await supabase
          .from("offer_page_products")
          .select(
            `
            offer_page_id,
            offer_pages!offer_page_id (
              id,
              heading,
              subheading,
              is_test,
              created_at
            )
          `
          )
          .eq("product_id", id);

        if (error) {
          console.error("Error fetching linked offer pages:", error);
        } else {
          const linkedPages =
            data?.map((item) => item.offer_pages).filter(Boolean) || [];
          console.log("Found linked offer pages:", linkedPages);
          setLinkedOfferPages(linkedPages as LinkedOfferPage[]);
        }
      } catch (err) {
        console.error("Error fetching linked offer pages:", err);
      } finally {
        setOfferPagesLoading(false);
      }
    };

    fetchLinkedOfferPages();
  }, [id, isAuthenticated, product]);

  const handleDuplicate = () => {
    toast({
      title: "Product duplicated",
      description: "A draft copy has been created.",
    });
  };

  const handleArchiveToggle = async () => {
    if (!product) {
      console.warn("No product available to archive.");
      return;
    }

    try {
      const newStatus = product.status === "active" ? "archived" : "active";
      await archiveProduct(product.id, newStatus);

      setProduct((prevProduct) => ({
        ...prevProduct,
        status: newStatus,
      }));
      toast({
        title:
          newStatus === "archived" ? "Product archived" : "Product activated",
        description: `The product has been ${
          newStatus === "archived" ? "archived" : "activated"
        } successfully.`,
      });
    } catch (err) {
      console.error("Failed to toggle product status:", err);
      toast({
        title: "Operation failed",
        description: "Failed to update product status.",
        variant: "destructive",
      });
    }
  };

  const handleCopyLink = () => {
    const paymentLink = getPaymentUrl(id);
    navigator.clipboard.writeText(paymentLink);
    toast({
      title: "Link copied!",
      description: "Payment link has been copied to clipboard.",
    });
  };

  const handleLogin = () => {
    navigate("/auth");
  };

  const calculateFees = (amount: number) => {
    const amountDollars = amount / 100;

    const stripeFee = calculateStripeFee(amountDollars, "card");
    const platformFee = calculatePlatformFee(amountDollars, "card");
    const grossCardResult = calculateGrossFromNetSync(amountDollars, "card");
    const grossCard = grossCardResult.grossAmount;

    const stripeFeeACH = calculateStripeFee(amountDollars, "bank");
    const grossACHResult = calculateGrossFromNetSync(amountDollars, "bank");
    const grossACH = grossACHResult.grossAmount;

    return {
      stripeFee,
      platformFee,
      grossCard,
      stripeFeeACH,
      grossACH,
    };
  };

  // Render authentication required state
  if (isAuthenticated === false) {
    return (
      <EntityDetailLayout title="Authentication Required" onClose={onClose}>
        <div className="flex flex-col items-center justify-center h-64 p-6">
          <ShieldAlert className="h-12 w-12 text-yellow-500 mb-4" />
          <h3 className="text-xl font-semibold mb-2">
            Authentication Required
          </h3>
          <p className="text-muted-foreground text-center mb-4">
            You need to be signed in to view product details.
          </p>
          <Button onClick={handleLogin} variant="default">
            Sign In
          </Button>
        </div>
      </EntityDetailLayout>
    );
  }

  // Render loading state
  if (loading) {
    return (
      <EntityDetailLayout
        title="Product Details"
        onClose={onClose}
        isLoading={true}
      >
        <div className="space-y-6 p-6">
          <div>
            <Skeleton className="h-8 w-3/4 mb-4" />
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-1/2 mb-2" />
          </div>
          <div>
            <Skeleton className="h-6 w-1/4 mb-3" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Skeleton className="h-24 w-full rounded-xl" />
              <Skeleton className="h-24 w-full rounded-xl" />
            </div>
          </div>
          <div>
            <Skeleton className="h-6 w-1/4 mb-3" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
        </div>
      </EntityDetailLayout>
    );
  }

  // Render error state
  if (error || !product) {
    return (
      <EntityDetailLayout title="Error" onClose={onClose}>
        <div className="flex flex-col items-center justify-center h-64 p-6">
          <ShieldAlert className="h-12 w-12 text-yellow-500 mb-4" />
          <h3 className="text-xl font-semibold mb-2 text-destructive">
            {error || "Product not found"}
          </h3>
          <p className="text-muted-foreground text-center mb-4">
            {error ===
            "Product not found or you do not have permission to view it."
              ? "You may not have permission to view this product, or it may have been deleted."
              : "We couldn't load the product details. It may have been deleted or you might not have permission to view it."}
          </p>
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </EntityDetailLayout>
    );
  }

  // Calculate fees when product is available
  const { grossCard, grossACH } = calculateFees(
    product.price_cents || product.amount || 0
  );

  // Format the footer buttons
  const renderFooter = () => (
    <div className="flex justify-between w-full space-x-2"></div>
  );

  return (
    <EntityDetailLayout title={null} onClose={onClose} footer={renderFooter()}>
      <div className="flex items-start justify-between mb-8">
        <div className="flex items-center gap-2">
          <h1 className="font-semibold text-3xl">
            {product.name || product.title || "Product Details"}
          </h1>
          <Badge
            variant={product.status === "active" ? "default" : "secondary"}
            className={
              product.status === "active"
                ? "bg-green-500 hover:bg-green-600"
                : "bg-gray-400 hover:bg-gray-500"
            }
          >
            {product.status?.charAt(0).toUpperCase() +
              product.status?.slice(1) || "Unknown"}
          </Badge>
        </div>
      </div>
      <div>
        {/* General Info Section */}
        <section className="mb-6 bg-[#F2F2F2] p-4 rounded-md ">
          <h3 className="text-lg font-semibold mb-4">General Information</h3>
          <div className="grid grid-cols-2 gap-y-3 text-sm">
            <div className="col-span-2">
              <h4 className="font-medium ">Net Price</h4>
              <p className="text-sm font-light text-foreground">
                {formatCurrency(product.price_cents || product.amount || 0)}
              </p>
            </div>
            <div className="col-span-2">
              <h4 className="font-medium">Est. Gross (Card)</h4>
              <p className="text-sm font-light text-foreground">
                {formatCurrency(grossCard)}
              </p>
            </div>
            <div className="col-span-2">
              <h4 className="font-medium">Est. Gross (ACH)</h4>
              <p className="text-sm font-light text-foreground">
                {formatCurrency(grossACH)}
              </p>
            </div>
            <div className="col-span-2">
              <h4 className="font-medium">Type</h4>
              <p className="text-sm font-light text-foreground">
                {product.billing_period === "one_time"
                  ? "One-time Payment"
                  : "Recurring Subscription"}
              </p>
            </div>
            <div className="col-span-2">
              <h4 className="font-medium">Created</h4>
              <p className="text-sm font-light text-foreground">
                {formatDate(product.created_at)}
              </p>
            </div>
            <div className="col-span-2">
              <h4 className="font-medium">ID</h4>
              <p className="font-mono text-xs text-foreground">{product.id}</p>
            </div>
          </div>
        </section>

        {/* Billing Details - Only for recurring products */}
        {product.billing_period !== "one_time" && (
          <section className="mb-6 bg-[#F2F2F2] p-4 rounded-md">
            <h3 className="text-lg font-semibold mb-4">Billing Details</h3>
            <div className="col-span-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium">Billing Interval</h4>
                  <p className="font-medium capitalize">
                    {product.billing_period}
                  </p>
                </div>
                {product.use_anchor_billing && (
                  <div>
                    <h4 className="font-medium">Anchor Billing</h4>
                    <p className="font-medium">1st of each month</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Description */}
        {product.description && (
          <section className="mb-6 bg-[#F2F2F2] p-4 rounded-md">
            <h3 className="text-lg font-semibold mb-4">Description</h3>
            <div className="col-span-2">
              <p className="text-muted-foreground">{product.description}</p>
            </div>
          </section>
        )}

      </div>
    </EntityDetailLayout>
  );
};

export default ProductDetailsView;
